import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { fetchChat } from '../services/chatApi';
import { getUnreadActionableMessages } from '../utils/chatReadState';

/** How often to refresh messages while a chat screen is open and visible. */
export const CHAT_LIVE_POLL_MS = 10000;
export const CHAT_LIVE_POLL_FAST_MS = 5000;

type ViewerRole = 'client' | 'sme';

type LatestMessage = {
    capabilityId: number;
    subCapabilityId: number;
    messageDate: string;
} | null;

type UseChatLivePollArgs = {
    userId: number | string | null;
    projectId: number | string | null;
    viewerRole: ViewerRole;
    setChatHistory: Dispatch<SetStateAction<any[]>>;
    setLatestMessage: Dispatch<SetStateAction<LatestMessage>>;
    enabled?: boolean;
    pollIntervalMs?: number;
    /** Called when a poll returns chat history that differs from the previous snapshot. */
    onHistoryUpdated?: (history: any[]) => void;
};

const getChatFingerprint = (history: any[] | null | undefined) => {
    if (!Array.isArray(history) || history.length === 0) {
        return '0';
    }

    let maxId = history[0]?.id ?? 0;
    for (let i = 1; i < history.length; i += 1) {
        const id = history[i]?.id ?? 0;
        if (id > maxId) {
            maxId = id;
        }
    }

    return `${history.length}:${maxId}`;
};

/**
 * Light in-chat polling: refetch history while the chat page is mounted and the tab is visible.
 * Outside chat, existing ~30s badge polling is unchanged. Stops when leaving the page or hiding the tab.
 */
export const useChatLivePoll = ({
    userId,
    projectId,
    viewerRole,
    setChatHistory,
    setLatestMessage,
    enabled = true,
    pollIntervalMs = CHAT_LIVE_POLL_MS,
    onHistoryUpdated,
}: UseChatLivePollArgs) => {
    const setChatHistoryRef = useRef(setChatHistory);
    const setLatestMessageRef = useRef(setLatestMessage);
    const onHistoryUpdatedRef = useRef(onHistoryUpdated);

    useEffect(() => {
        setChatHistoryRef.current = setChatHistory;
        setLatestMessageRef.current = setLatestMessage;
        onHistoryUpdatedRef.current = onHistoryUpdated;
    }, [setChatHistory, setLatestMessage, onHistoryUpdated]);

    useEffect(() => {
        if (!enabled || !userId || !projectId) {
            return undefined;
        }

        let cancelled = false;
        let intervalId: number | null = null;

        const pollChatHistory = async () => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
                return;
            }

            try {
                const chatData = await fetchChat({ userId, projectId });
                if (cancelled || !Array.isArray(chatData)) {
                    return;
                }

                const nextFingerprint = getChatFingerprint(chatData);

                let historyChanged = false;
                setChatHistoryRef.current((prev) => {
                    if (getChatFingerprint(prev) === nextFingerprint) {
                        return prev;
                    }
                    historyChanged = true;
                    return chatData;
                });

                if (historyChanged) {
                    onHistoryUpdatedRef.current?.(chatData);
                }

                const unreadActionable = getUnreadActionableMessages(
                    chatData,
                    userId,
                    projectId,
                    viewerRole
                );

                if (unreadActionable.length > 0) {
                    const latestUnread = unreadActionable[0];
                    setLatestMessageRef.current((prev) => {
                        if (
                            prev &&
                            prev.capabilityId === latestUnread.capabilityId &&
                            prev.subCapabilityId === latestUnread.subCapabilityId &&
                            prev.messageDate === latestUnread.messageDate
                        ) {
                            return prev;
                        }

                        return {
                            capabilityId: latestUnread.capabilityId,
                            subCapabilityId: latestUnread.subCapabilityId,
                            messageDate: latestUnread.messageDate,
                        };
                    });
                } else {
                    setLatestMessageRef.current((prev) => (prev == null ? prev : null));
                }
            } catch (error) {
                console.error('Error polling chat history:', error?.message || error);
            }
        };

        const startPolling = () => {
            if (intervalId != null) {
                return;
            }
            intervalId = window.setInterval(pollChatHistory, pollIntervalMs);
        };

        const stopPolling = () => {
            if (intervalId == null) {
                return;
            }
            window.clearInterval(intervalId);
            intervalId = null;
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                void pollChatHistory();
                startPolling();
            } else {
                stopPolling();
            }
        };

        if (typeof document === 'undefined' || document.visibilityState === 'visible') {
            void pollChatHistory();
            startPolling();
        }

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            cancelled = true;
            stopPolling();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [enabled, userId, projectId, viewerRole, pollIntervalMs]);
};
